import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/auth';
import Button from './ui/Button';
import { Save, Loader2, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DROPDOWN_OPTIONS, getStatusConfig } from '../lib/appointmentStatus';
import { getClients, getSdrs } from '../lib/api';

interface AppointmentEditFormProps {
  appointment: any;
  onSave: (updatedAppointment: any) => void;
  onCancel: () => void;
}

const AppointmentEditForm: React.FC<AppointmentEditFormProps> = ({
  appointment,
  onSave,
  onCancel
}) => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [sdrs, setSdrs] = useState<any[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  useEffect(() => {
    console.log('Initial appointment data:', {
      id: appointment.id,
      ...appointment
    });
    fetchProfile();
    fetchClients();
    fetchSdrs();
  }, [appointment]);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      console.log('Profile loaded:', data);
      setProfile(data);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchClients = async () => {
    try {
      const data = await getClients();
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSdrs = async () => {
    try {
      const data = await getSdrs();
      setSdrs(data || []);
    } catch (error) {
      console.error('Error fetching SDRs:', error);
    }
  };

  const [formData, setFormData] = useState({
    title: appointment.title || '',
    date: appointment.date || '',
    time: appointment.time || '',
    status: appointment.status || 'planifié',
    prospect_name: appointment.prospect_name || '',
    prospect_linkedin: appointment.prospect_linkedin || '',
    job_title: appointment.job_title || '',
    company_name: appointment.company_name || '',
    company_linkedin: appointment.company_linkedin || '',
    phone: appointment.phone || '',
    description: appointment.description || '',
    location: appointment.location || '',
    notes: appointment.notes || '',
    client_id: appointment.client_id || '',
    sdr_id: appointment.sdr_id || ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSuccessMessage(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    console.log('Updating appointment with ID:', appointment.id);

    try {
      const updateData = {
        ...Object.fromEntries(
          Object.entries(formData).filter(([_, value]) => value !== undefined)
        ),
        updated_at: new Date().toISOString()
      };

      console.log('Updating appointment with data:', updateData);

      const { data, error: updateError } = await supabase
        .from('appointments')
        .update(updateData)
        .eq('id', appointment.id)
        .select()
        .maybeSingle();

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error(`Failed to update appointment: ${updateError.message}`);
      }

      console.log('Update response:', data);

      if (!data) {
        throw new Error('Updated appointment not found');
      }

      setSuccessMessage('Rendez-vous mis à jour avec succès');
      onSave(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating the appointment');
      console.error('Update error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ? Cette action est irréversible.');
    console.log('🤔 User confirmed deletion:', confirmDelete);

    if (!confirmDelete) {
      console.log('❌ User cancelled deletion');
      return;
    }

    setIsDeleting(true);
    setError(null);
    setSuccessMessage(null);

    console.log('🗑️ Starting deletion process for appointment:', appointment.id);

    try {
      const { data: existingAppointment, error: checkError } = await supabase
        .from('appointments')
        .select('id, title, client_id')
        .eq('id', appointment.id)
        .maybeSingle();

      if (checkError) {
        console.error('❌ Error checking appointment existence:', checkError);
        throw new Error(`Impossible de vérifier l'existence du rendez-vous: ${checkError.message}`);
      }

      if (!existingAppointment) {
        throw new Error('Le rendez-vous n\'existe plus dans la base de données');
      }

      console.log('✅ Appointment exists, proceeding with deletion:', existingAppointment);

      const { error: deleteError } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appointment.id);

      if (deleteError) {
        console.error('❌ Delete error:', deleteError);

        if (deleteError.code === 'PGRST301') {
          throw new Error('Vous n\'avez pas les permissions pour supprimer ce rendez-vous');
        } else if (deleteError.code === 'PGRST116') {
          throw new Error('Le rendez-vous n\'existe plus');
        } else {
          throw new Error(`Erreur lors de la suppression: ${deleteError.message} (Code: ${deleteError.code})`);
        }
      }

      console.log('✅ Appointment deleted successfully');

      setSuccessMessage('Rendez-vous supprimé avec succès. Redirection...');

      setTimeout(() => {
        navigate('/appointments');
      }, 1500);

    } catch (err: any) {
      console.error('❌ Delete operation failed:', err);
      setError(err.message || 'Une erreur est survenue lors de la suppression du rendez-vous');
    } finally {
      setIsDeleting(false);
    }
  };

  const isAdmin = !isLoadingProfile && profile && (profile.role === 'admin' || profile.role === 'super_admin');

  console.log('AppointmentEditForm - Debug info:', {
    isLoadingProfile,
    profile,
    profileRole: profile?.role,
    isAdmin,
    clients: clients.length
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <label>
            <span className="block text-sm font-medium text-gray-700">Title</span>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              required
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Date</span>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              required
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Time</span>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              required
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Status</span>
            <select
              name="status"
              value={getStatusConfig(formData.status).key}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
            >
              {DROPDOWN_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          {isLoadingProfile ? (
            <div className="text-sm text-gray-500">Chargement du profil...</div>
          ) : !profile ? (
            <div className="text-sm text-red-500">Erreur: Profil non chargé</div>
          ) : !isAdmin ? (
            <div className="text-sm text-orange-500">Rôle actuel: {profile.role} (Admin requis)</div>
          ) : (
            <>
              <label>
                <span className="block text-sm font-medium text-gray-700">Client</span>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="block text-sm font-medium text-gray-700">SDR Assigné</span>
                <select
                  name="sdr_id"
                  value={formData.sdr_id}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
                >
                  <option value="">Aucun SDR</option>
                  {sdrs.map(sdr => (
                    <option key={sdr.user_id} value={sdr.user_id}>
                      {sdr.sales_name} {sdr.squads?.name && `(${sdr.squads.name})`}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>

        <div className="space-y-4">
          <label>
            <span className="block text-sm font-medium text-gray-700">Prospect Name</span>
            <input
              type="text"
              name="prospect_name"
              value={formData.prospect_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              required
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Prospect LinkedIn</span>
            <input
              type="url"
              name="prospect_linkedin"
              value={formData.prospect_linkedin}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Job Title</span>
            <input
              type="text"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              placeholder="Poste du prospect"
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Company Name</span>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
              required
            />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Company LinkedIn</span>
            <input
              type="url"
              name="company_linkedin"
              value={formData.company_linkedin}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
            />
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <label>
          <span className="block text-sm font-medium text-gray-700">Phone</span>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
          />
        </label>
        <label>
          <span className="block text-sm font-medium text-gray-700">Location</span>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
          />
        </label>
        <label>
          <span className="block text-sm font-medium text-gray-700">Description</span>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
          />
        </label>
        <label>
          <span className="block text-sm font-medium text-gray-700">Notes</span>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-pink-500 focus:outline-none focus:ring-pink-500 sm:text-sm"
          />
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button
          type="button"
          variant="danger"
          onClick={handleDelete}
          disabled={isLoading || isDeleting}
          icon={isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {isDeleting ? 'Suppression...' : 'Supprimer'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading || isDeleting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || isDeleting} icon={isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}>
          Save Changes
        </Button>
      </div>
    </form>
  );
};

export default AppointmentEditForm;
